import { Action, Observable, Reducer } from 'redux'
import { ActionsObservable, ofType, StateObservable } from 'redux-observable'
import * as R from 'ramda'
import { from, iif, of } from 'rxjs'
import { catchError, delay, filter, map, mergeMap, withLatestFrom } from 'rxjs/operators'
import { ApplicationState, AppThunkAction } from '.'
import { ConfigurationClient, ConfigurationSettingsRequestDTO, ConfigurationSettingsResponseDTO, TransportConfigurationSettings, TransportEclypseIpSetting, TransportModemIpSetting } from '../generated/ApiClient'
import { ToRequest, ToTransport } from '../generated/ApiClientExtensions'

export const PagingAmount = 10

export interface TransportAppState {
    isLoading: boolean
    transportConfig: TransportConfigurationSettings | undefined
    lastHighestIp: number
}

interface RequestTransportConfigAction {
    type: 'transporter/REQUEST_CONFIG'
}

interface RetryTransportConfigAction {
    type: 'transporter/RETRY_CONFIG'
}

interface ResponseTransportConfigAction {
    type: 'transporter/RECEIVE_CONFIG'
    transportConfig: TransportConfigurationSettings
}

interface AddOrUpdateIpSettingAction {
    type: 'transporter/ADD_OR_UPDATE_IP_SETTINGS'
    ipSettings: [string | undefined, TransportModemIpSetting | TransportEclypseIpSetting][]
}

interface RemoveIpSettingAction {
    type: 'transporter/REMOVE_IP_SETTINGS'
    ipSettings: [string, TransportModemIpSetting | TransportEclypseIpSetting][]
}

type TransportAppAction = RequestTransportConfigAction | RetryTransportConfigAction | ResponseTransportConfigAction | AddOrUpdateIpSettingAction | RemoveIpSettingAction

/**
 * development port odin-web uses
 */
const DevPort = 44351
/**
 * randomly generated number not in this list
 * https://en.wikipedia.org/wiki/List_of_TCP_and_UDP_port_numbers
 */
const LocalPort = 45773
const LocalURL = (p: number) => `https://localhost:${p}`

export const refreshConfigEpic = (action$: ActionsObservable<TransportAppAction>) => {
    const getFrom = (port: number) => {
        const client = new ConfigurationClient(LocalURL(port))
        return from(client.get()).pipe(
            map(s => ({ type: 'transporter/RECEIVE_CONFIG', transportConfig: ToTransport(s) })))
    }
    // if we don't find the config, try on the dev port. If neither succeeds, retry. This would occur if a user hasn't installed the application yet
    return action$.pipe(
        ofType('transporter/REQUEST_CONFIG', 'transporter/RETRY_CONFIG'),
        mergeMap(() =>
            getFrom(LocalPort).pipe(
                catchError((err, caught) =>
                    iif(() => process.env.NODE_ENV !== 'production', getFrom(DevPort)).pipe(
                        catchError((e, c) =>
                            of({ type: 'transporter/RETRY_CONFIG' })
                                .pipe(delay(15000)))
                    )
                )
            )
        )
    )
}

export const sendConfigEpic = (action$: ActionsObservable<TransportAppAction>, state$: StateObservable<ApplicationState>) => {
    const set = (port: number, config: ConfigurationSettingsRequestDTO) => {
        const client = new ConfigurationClient(LocalURL(port))
        return from(client.set(config)).pipe(
            map(s => ({ type: 'transporter/RECEIVE_CONFIG', transportConfig: ToTransport(s) })))
    }
    return action$.pipe(
        ofType('transporter/ADD_OR_UPDATE_IP_SETTINGS', 'transporter/REMOVE_IP_SETTINGS'),
        withLatestFrom(state$),
        // only send updates if we have the transport config. We should have the config by now, as no UI elements should allow changing the config if we don't already have it
        filter(([, state]) => !!state.transportAppState?.transportConfig),
        mergeMap(([, state]) => {
            const url = `${window.location.origin}/api/RelayApi/forwardRequest`
            // clear out endpoint and authentication code for now in develop so that they don't overwrite the settings that are correct for the azure function
            const config = { ...ToRequest(state.transportAppState!.transportConfig!), cloudEndpoint: url }
            return set(LocalPort, config).pipe(
                catchError((err, caught) =>
                    iif(() => process.env.NODE_ENV !== 'production', set(DevPort, config)).pipe(
                        catchError((e, c) => of({ type: 'transporter/RETRY_CONFIG' }))
                    )
                )
            )
        })
    )
}

export const actionCreators = {
    requestTransportConfig: (): AppThunkAction<TransportAppAction> => (dispatch, getState) => {
        const appState = getState()
        if (appState && appState.transportAppState && (!appState.transportAppState.isLoading || !appState.transportAppState.transportConfig)) {
            dispatch({ type: 'transporter/REQUEST_CONFIG' })
        }
    },
    addOrUpdateIpSettings: (ipSettings: [string | undefined, TransportModemIpSetting | TransportEclypseIpSetting][]): AppThunkAction<TransportAppAction> => (dispatch, getState) => {
        const config = getState().transportAppState
        if (config && config?.transportConfig) {
            dispatch({ type: 'transporter/ADD_OR_UPDATE_IP_SETTINGS', ipSettings })
        }
    },
    removeIpSettings: (ipSettings: [string, TransportModemIpSetting | TransportEclypseIpSetting][]): AppThunkAction<TransportAppAction> => (dispatch, getState) => {
        const modems = getState().transportAppState?.transportConfig?.simulatorToRemoteIpMap
        if (modems) {
            dispatch({ type: 'transporter/REMOVE_IP_SETTINGS', ipSettings })
        }
    }
}

const unloadedState = { isLoading: false, transportConfig: undefined, lastHighestIp: 1 }

export const reducer: Reducer<TransportAppState> = (state: TransportAppState | undefined, incomingAction: Action): TransportAppState => {
    if (state === undefined)
        return unloadedState

    const action = incomingAction as TransportAppAction
    switch (action.type) {
        case 'transporter/REQUEST_CONFIG':
            return {
                ...state,
                transportConfig: undefined,
                isLoading: true
            }
        case 'transporter/RETRY_CONFIG':
            return {
                ...state,
                isLoading: false
            }
        case 'transporter/ADD_OR_UPDATE_IP_SETTINGS':
            if (state.transportConfig !== undefined) {
                // there's an issue with the transport app, where it cannot start a new listener on the same IP, even if it has already been stopped
                // const usedIps = Object.entries(modems).map(([ip, _]) => ip)
                // const withIps = R.zip(R.difference(R.range(2, 254).map(s => `127.0.0.${s}`), usedIps), ipSettings).map<[string, TransportModemIpSetting | TransportEclypseIpSetting]>(([ip, [_, setting]]) => [ip, setting])
                const numbers = Object.entries(state.transportConfig.simulatorToRemoteIpMap).map(([ip, _]) => parseInt(ip.slice(ip.lastIndexOf('.') + 1)))
                const highestIp = R.reduce(Math.max, state.lastHighestIp, numbers) + 1
                const withIps = R.zip(R.range(highestIp, 254).map(s => `127.0.0.${s}`), action.ipSettings).map(([ip, [_, setting]]) => [ip, setting])
                return {
                    ...state,
                    lastHighestIp: highestIp,
                    transportConfig: {
                        ...state.transportConfig,
                        simulatorToRemoteIpMap: {
                            ...state.transportConfig.simulatorToRemoteIpMap,
                            ...Object.fromEntries(withIps)
                        }
                    }
                }
            }
            break
        case 'transporter/REMOVE_IP_SETTINGS':
            if (state.transportConfig !== undefined)
                return {
                    ...state,
                    transportConfig: {
                        ...state.transportConfig,
                        simulatorToRemoteIpMap:
                            // delete entries in simulators that are in ipSettings
                            Object.fromEntries(R.difference(Object.entries(state.transportConfig.simulatorToRemoteIpMap), action.ipSettings))
                    }
                }
            break
        case 'transporter/RECEIVE_CONFIG':
            return {
                ...state,
                transportConfig: action.transportConfig,
                isLoading: false
            }
    }
    return state
}